import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import AlarmIcon from 'shared-vectors/icons/BellFilled';
import NoAlarmIcon from 'shared-vectors/icons/BellFilledOff';

import StaredIcon from 'shared-vectors/icons/Stared';
import StarIcon from 'shared-vectors/icons/Star';

import { setItem, getItem } from 'shared-utils/src/user/helpers';
import Grid from 'shared-components/components/Grid';

import { Icon } from 'shared-vectors';
import trackGTMEvents from 'shared-utils/src/trackGTMEvents/new';
import openSaveSearchModal from 'shared-utils/src/saveSearch/openSaveSearchModal';
import sleep from 'shared-utils/src/sleep';
import loadSearchIconDynamically from 'shared-utils/src/loadSearchIconDynamically';
import { useCtxGlobal } from '@client/app/Container';
import {
  saveSearchQb,
  createGenericAfterSpecificModal,
  createFeedbackModal,
  createNewSaveSearchFeedbackModal,
  createConsentRequestModal,
} from '@modals/factories';

import computeDifferenceinHours from 'client/helpers/computeDifferenceinHours';

import HintTooltip from './HintTooltip';

import './critical.scss';

const SaveSearchButton = ({
  publisherName,
  setForcedAuction,
  checkProposeBkmAfterLead,
  userLogIn,
  notShownOn,
  positionGTM,
  showSaveSearchBannerGreenHint: cmpLoaded,
  mapData,
  copySaveSearch: {
    title,
    editTitle,
  },
  disabled,
  bbPolygon,
}) => {
  const {
    openModal,
    closeModal,
    globalState: {
      config: {
        reCAPTCHA,
        cookiesConfig = {},
        api: { account, authBasePrefix },
      } = {},
    } = {},
    globalSelector,
    globalReducer,
    isMobile,
    siteUrl,
    saveBookmarkSuccess,
    filtersProps,
    isSmallMobile,
    setNewSearchFromSaveSearchModal,
    pageType,
    model: {
      total,
      paginator: { currentPage },
      seoDataHeader,
    } = {},
    user,
    userConf: {
      userUrls: { userhost },
    },
    getSearchesList,
    constantsCopy,
    constantsCopy: {
      SRP_EDIT_SEARCH_NO_ALARM_ICON: iconEditNoAlarm,
      SRP_EDIT_SEARCH_ICON: iconEditAlarm,
      SRP_SAVE_SEARCH_ICON: iconSave,
      SRP_TOOLTIP_BTN_SAVE_SEARCH_MAIN,
      SRP_TOOLTIP_BTN_SAVE_SEARCH_DESCR,
    },
    logIn,
    updateNewVerifiedUser,
  } = useCtxGlobal();

  const buttonRef = useRef(null);
  const showMCFakeTooltipHasBeenClosed = useRef(false);

  const searchIsSaved = globalSelector('searchIsSaved');
  const searchIsSavedButNotActive = globalSelector('searchIsSavedButNotActive');
  const firstCallForSaveSearchFinished = globalSelector('firstCallForSaveSearchFinished');
  const bookmarkData = globalSelector('bookmarkData');
  const hasAlert = bookmarkData &&
    bookmarkData?.alerts?.some((a) => a.alertFrequency !== 'never');

  const clsNotShowOn = notShownOn === 'none' ? '' : `not-show-on-${notShownOn}`;
  const [showTooltip, setShowTooltip] = useState(false);
  const [showMCFakeTooltip, setShowMCFakeTooltip] = useState(false);
  
  const [titleHint, setTitleHint] = useState(SRP_TOOLTIP_BTN_SAVE_SEARCH_MAIN);
  const [subTitleHint, setSubTitleHint] = useState(
    SRP_TOOLTIP_BTN_SAVE_SEARCH_DESCR,
  );

  const proposeBkmBaloon = globalSelector('proposeBkmBaloon');

  useEffect(() => {
    if (searchIsSavedButNotActive && computeDifferenceinHours(getItem('hintTooltipMCFakeTooltip'), 6)) {
      if (isMobile && positionGTM === 'SRP_PageTitle') {

        const scrollHandler = () => {
          if (!showMCFakeTooltipHasBeenClosed.current) {
            if (buttonRef?.current?.getBoundingClientRect()?.y < 9) {
              setShowMCFakeTooltip(true);
            } else {
              setShowMCFakeTooltip(false);
            }
          }
        };
        window.addEventListener('scroll', () => scrollHandler());
      } else if (!isMobile && positionGTM === 'SRP_StickyHeader') {
        setShowMCFakeTooltip(true);
      }
    }
  }, [isMobile, positionGTM, searchIsSavedButNotActive, showMCFakeTooltipHasBeenClosed]);

  useEffect(() => {
    if (user?.isAuthenticated && showMCFakeTooltip) {
      setShowMCFakeTooltip(false);
    }
  }, [user]);

  useEffect(() => {
    const st = getItem('hintTooltipSaveSearch');
    if (
      !isMobile &&
      cmpLoaded &&
      firstCallForSaveSearchFinished
    ) {
      try {
        const twelveHoursInMS = 12 * 60 * 60 * 1000; // 12 ore in millisecondi
        const twelveHourseAgo = Date.now() - twelveHoursInMS;
        const timeSaved = parseInt(st, 10) || 0;
        const isLessThan12Hours = twelveHourseAgo > timeSaved;
        if (!searchIsSaved && !searchIsSavedButNotActive && st === 'fromProposeSaveSearch' && total > 0) {
          setTitleHint('Attiva le notifiche');
          setSubTitleHint('e non perderti nessuna occasione!');
          setShowTooltip(true);
        } else if (
          isLessThan12Hours &&
          currentPage === 1 &&
          total > 0 &&
          total <= 500
        ) {
          if (searchIsSaved || searchIsSavedButNotActive) {
            if (searchIsSaved) {
              const ct = Date.now();
              setItem('hintTooltipSaveSearch', ct);
            }
            setShowTooltip(false);
          } else {
            setShowTooltip(true);
            sleep(300).then(() => trackGTMEvents({
              category: 'FavouriteAndAlert',
              action: 'ShowSaveSearchBanner',
              ...proposeBkmBaloon?.from && { from: proposeBkmBaloon.from }, 
            }, {
              paEventType: 'self_promotion.impression',
              paAdditionalCdimension: {
                onsitead_type: 'Self promotion',
              },
            }, true));
          }
        }
      } catch (error) {
        setShowTooltip(true);
      }
    } else if (isMobile) {
      setShowTooltip(false);
    }
  }, [cmpLoaded, total, isMobile, searchIsSaved, searchIsSavedButNotActive, firstCallForSaveSearchFinished]);

  const getProposeDetails = () => {

    const isFromPropose = !!proposeBkmBaloon;
    const propose = isFromPropose
      ? {
        from: proposeBkmBaloon.from,
        searchType: ['fromProposeServer', 'exactSearch'].includes(
          proposeBkmBaloon.value,
        )
          ? 'UserSearch'
          : 'GenericSearch',
      }
      : {};

    return { isFromPropose, propose };
  };

  const hideTooltipAndSetLS = () => {
    const ct = Date.now();
    if (showMCFakeTooltip) {
      setShowMCFakeTooltip(false);
      setItem('hintTooltipMCFakeTooltip', ct);
      showMCFakeTooltipHasBeenClosed.current = true;
    } else {
      setShowTooltip(false);
      setItem('hintTooltipSaveSearch', ct);
    }
  };

  const saveSearch = () => {
    const { isAuthenticated } = user;
    const { urls = {} } = seoDataHeader;
    const { srp = '' } = urls;
    const activeNearby = globalSelector('activeNearby');
    const poiNamesList = globalSelector('poiNamesList');
    const ct = Date.now();
    setShowTooltip(false);
    setItem('hintTooltipSaveSearch', ct);
    const deleteBookmarkSuccess = () => {
      globalReducer('deleteBookmark', null);
      checkProposeBkmAfterLead && checkProposeBkmAfterLead();
    };
    const disableProposeBkm = () => globalReducer('disableProposeBkmAfterLead');
    const closeModalAction = payload => closeModal(payload.close);
    openSaveSearchModal({
      openModalAction: openModal,
      closeModalAction,
      afterClose: !isAuthenticated ? updateNewVerifiedUser : null,
      createNewSaveSearchModal: saveSearchQb,
      createGenericAfterSpecificModal,
      createNewSaveSearchFeedbackModal,
      createConsentRequestModal,
      createFeedbackModal,
      getSearchesList,
      isMobile,
      isSmallMobile,
      filtersProps,
      copyModalTitle: constantsCopy,
      saveSearchProps: {
        domain: siteUrl,
        mycasaDomain: userhost,
        accountDomain: account,
        portalDomain: siteUrl,
        authBasePrefix,
        app: 'P18',
        disableProposeBkm,
        activeNearby,
        poiNamesList,
        searchIsSaved,
        bookmarkData,
        publisherName,
        setForcedAuction,
        saveBookmarkSuccess,
        deleteBookmarkSuccess,
        setNewSearchFromSaveSearchModal,
        total,
        isFromNotRegisteredUser: !isAuthenticated,
        user,
        logIn: userLogIn,
        reCAPTCHA,
        cookiesConfig,
        positionGTM,
        pageType,
        mapData,
        bbPolygon,
        urlLSSavedSearch: srp,
        ...getProposeDetails(),
      },
      panelChooseBy: {
        isMobile,
        actionClicked: 'srpSaveSearch',
        pageFrom: pageType,
      },
    });
  };

  const saveSearchEventLabel = () => {
    if (!bookmarkData) {
      return 'notSavedYet';
    }
    const { alerts } = bookmarkData;
    if (
      !alerts.mail ||
      alerts.mail.alertFrequency === 'never' ||
      !alerts.push ||
      alerts.push.alertFrequency === 'never'
    ) {
      return 'isFavourite';
    }
    return 'hasAlert';
  };

  const saveSearchProxy = (e) => {
    e.preventDefault();

    if (searchIsSavedButNotActive) {
      logIn(() => {}, 'AddFavouriteSearch', 'ActivateSearchButton', { mustRegister: true });
      return;
    }

    const { isFromPropose, propose } = getProposeDetails();

    const evt = {
      category: 'FavouriteAndAlert',
      action: 'OpenAlertModal',
      position: positionGTM,
      label:
        isFromPropose || !searchIsSaved
          ? 'notSavedYet'
          : saveSearchEventLabel(bookmarkData),
    };

    if (isFromPropose) {
      evt.from = propose.from;
    }

    sleep(300).then(() => trackGTMEvents(evt, {}, true));

    saveSearch();
  };

  const iconMap = {
    Alarm: AlarmIcon,
    NoAlarm: NoAlarmIcon,
    AlarmEmpty: AlarmIcon,
    Star: StarIcon,
    Stared: StaredIcon,
  };
  const { iconName } = loadSearchIconDynamically({
    iconEditNoAlarm,
    iconSave,
    iconEditAlarm,
    hasAlerts: hasAlert,
    searchIsSaved,
  });

  const defaultButton = (
    <button
      type="button"
      aria-label="Salva la ricerca"
      className={`save${disabled ? ' disabled' : ''}`}
      data-action="saveSearch"
      onClick={saveSearchProxy}
    >
      <Grid
        taghtml="span"
        className="btn-save-search-cnt-icon"
        align="center"
        justify="center"
      >
        <Icon name={iconMap[iconName]} width="24px" fill />
      </Grid>
      <span className="tp-w--l tp-s--l ml--xs">{title}</span>
    </button>
  );

  const savedButton = (
    <button
      type="button"
      aria-label="Salva la ricerca"
      className={`btn-save-search-saved c-bg--w${disabled ? ' disabled' : ''}`}
      data-action="saveSearch"
      onClick={saveSearchProxy}
    >
      <Grid
        taghtml="span"
        className={`btn-save-search-cnt-icon${!hasAlert ? '-no-alarm' : ''}`}
        align="center"
        justify="center"
      >
        <Icon name={iconMap[iconName]} width="24px" fill />
      </Grid>
      <span className="text tp-s--l tp-w--m c-txt--f0 ml--xs">
        {editTitle}
      </span>
    </button>
  );

  const savedNotActiveButton = (
    <button
      ref={notShownOn === 'desktop' ? buttonRef : null}
      type="button"
      aria-label="Attiva la ricerca"
      className={`save${disabled ? ' disabled' : ''}`}
      data-action="saveSearch"
      onClick={saveSearchProxy}
    >
      <span className="tp-w--l tp-s--l ml--xs">Attiva ricerca</span>
    </button>
  );

  let button = defaultButton;
  if (searchIsSaved) {
    button = savedButton;
  } else if (searchIsSavedButNotActive) {
    button = savedNotActiveButton;
  }

  return (
    <div className={`btn-save-search ${clsNotShowOn}`}>
      {button}      
      {(showTooltip || showMCFakeTooltip) && (
        <HintTooltip
          visible={showMCFakeTooltip ? true : !isMobile}
          title={titleHint}
          subTitle={subTitleHint}
          hideTooltip={hideTooltipAndSetLS}
          isMCFakeTooltip={showMCFakeTooltip}
          isMobile={isMobile}
          proposeBkmBaloon={proposeBkmBaloon}
        />
      )}
    </div>
  );
};

SaveSearchButton.propTypes = {
  publisherName: PropTypes.string,
  setForcedAuction: PropTypes.func,
  checkProposeBkmAfterLead: PropTypes.func,
  userLogIn: PropTypes.func,
  notShownOn: PropTypes.string,
  positionGTM: PropTypes.string,
  showSaveSearchBannerGreenHint: PropTypes.bool,
  copySaveSearch: PropTypes.object,
  mapData: PropTypes.object,
  disabled: PropTypes.bool,
  bbPolygon: PropTypes.string,
};

SaveSearchButton.defaultProps = {
  publisherName: '',
  setForcedAuction: () => { },
  checkProposeBkmAfterLead: () => { },
  userLogIn: () => { },
  notShownOn: 'none',
  positionGTM: '',
  showSaveSearchBannerGreenHint: false,
  copySaveSearch: {},
  mapData: null,
  disabled: false,
  bbPolygon: '',
};

export default SaveSearchButton;
